import { autoBuildHierarchy } from "@/apis/table/route";
import { useTableId } from "@/components/mapping/hooks";
import queries from "@/queries";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useWorkbookId } from "../use-workbook";

export const useAutoBuildHierarchy = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { data: workbookId, isLoading: isWorkbookIdLoading } = useWorkbookId();
  const { data: tableId, isLoading: isTableIdLoading } = useTableId();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationKey: ["auto-build-hierarchy", workbookId, tableId?.tableId],
    mutationFn: async (orderOnly: boolean = false) => {
      if (!workbookId) {
        throw new Error("Workbook ID is not found");
      }
      if (!tableId?.tableId) {
        throw new Error("Table ID is not found");
      }
      return autoBuildHierarchy(workbookId, tableId.tableId, orderOnly);
    },
    onSuccess: () => {
      if (!workbookId || !tableId?.tableId) return;

      // 관련 쿼리 갱신
      queryClient.invalidateQueries({
        queryKey: queries
          .workbook(workbookId)
          ._ctx.tables(tableId.tableId)
          ._ctx.editablePreview().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queries
          .workbook(workbookId)
          ._ctx.tables(tableId.tableId)
          ._ctx.editableTreeView().queryKey,
      });

      toast.success("위계가 자동으로 설정되었습니다.");
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error("자동 빌딩 중 오류가 발생했습니다.");
      options?.onError?.(error);
    },
  });

  return {
    mutate,
    mutateAsync,
    isPending,
    ready:
      !isWorkbookIdLoading &&
      !isTableIdLoading &&
      !!workbookId &&
      !!tableId?.tableId,
  };
};
